import React, { Component } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HomeIcon from '@mui/icons-material/Home';
import Messages from '../../constants/messages';

const styles = {
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#e5e5e5',
        padding: '20px',
        fontFamily: "'Roboto', 'Barlow', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        zIndex: 9999,
        overflow: 'auto'
    },
    content: {
        background: 'transparent',
        padding: '40px',
        maxWidth: '800px',
        textAlign: 'center'
    },
    icon: {
        marginBottom: '48px',
        display: 'inline-block'
    },
    title: {
        color: '#333',
        fontSize: '24px',
        fontWeight: 400
    },
    message: {
        color: '#666',
        fontSize: '14px',
        lineHeight: 1.6,
        margin: '16px 0 32px 0'
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
    },
    button: {
        padding: '10px 24px',
        border: '2px solid #b933cc',
        borderRadius: '20px',
        backgroundColor: 'transparent',
        color: '#b933cc',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    details: {
        marginTop: '32px',
        textAlign: 'left',
        borderTop: '1px solid #e0e0e0',
        paddingTop: '24px'
    },
    summary: {
        cursor: 'pointer',
        fontWeight: 500,
        color: '#666',
        marginBottom: '16px',
        userSelect: 'none'
    },
    stack: {
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        padding: '16px',
        fontSize: '12px',
        overflowX: 'auto'
    },
    stackStrong: {
        display: 'block',
        marginTop: '12px',
        marginBottom: '8px',
        color: '#333'
    },
    stackPre: {
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        fontFamily: "'Courier New', monospace",
        color: '#d32f2f',
        lineHeight: 1.4
    }
};

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            buttonHovered: false
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error,
            errorInfo
        });

        if (!sessionStorage.getItem('errorBoundaryRefreshed')) {
            sessionStorage.setItem('errorBoundaryRefreshed', 'true');
            window.location.reload();
        }
    }

    handleGoHome = () => {
        window.location.href = '#/';
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            const buttonStyle = this.state.buttonHovered 
                ? { ...styles.button, backgroundColor: 'rgba(185, 51, 204, 0.05)', transform: 'translateY(-1px)' }
                : styles.button;

            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <div style={styles.icon}>
                            <WarningAmberIcon style={{ fontSize: 80, color: '#F5A623' }} />
                        </div>
                        <div>
                            <h1 style={styles.title}>{Messages.errorBoundary.title}</h1>
                            <p style={styles.message}>
                                {Messages.errorBoundary.message}
                            </p>
                        </div>
                        <div style={styles.actions}>
                            <button 
                                style={buttonStyle}
                                onClick={this.handleGoHome}
                                onMouseEnter={() => this.setState({ buttonHovered: true })}
                                onMouseLeave={() => this.setState({ buttonHovered: false })}
                            >
                                <HomeIcon />
                                {Messages.errorBoundary.goHomeButton}
                            </button>
                        </div>

                        {process.env.NODE_ENV != 'production' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>{Messages.errorBoundary.errorDetailsTitle}</summary>
                                <div style={styles.stack}>
                                    <strong style={styles.stackStrong}>{Messages.errorBoundary.errorLabel}</strong>
                                    <pre style={styles.stackPre}>{this.state.error.toString()}</pre>
                                    {this.state.errorInfo && (
                                        <>
                                            <strong style={styles.stackStrong}>{Messages.errorBoundary.componentStackLabel}</strong>
                                            <pre style={styles.stackPre}>{this.state.errorInfo.componentStack}</pre>
                                        </>
                                    )}
                                </div>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
